"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { Controller, useForm } from "react-hook-form";
import { RegisterFormSchema } from "./schema";
import { z } from "zod";
import { Form, FormMessage } from "@/components/ui/form";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import hooks from "@/hooks";
import PhoneInput from "react-phone-input-2";
import { useToast } from "@/hooks/use-toast";
import { AxiosError } from "axios";
import { useRouter } from "next/navigation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ptBRCountries } from "@/consts/PtBRCountries";

type Register = z.infer<typeof RegisterFormSchema>;

const RegisterForm = () => {
  const t = useTranslations("Auth.RegisterPage.form");

  const { toast } = useToast();
  const router = useRouter();

  const form = useForm<Register>({
    resolver: zodResolver(RegisterFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      user_intent: "",
    },
  });

  const registerService = hooks.useRegister({
    onError: (error: unknown) => {
      if ((error as Error).message === "NEXT_REDIRECT") return;
      toast({
        title: "Falha ao efetuar o cadastro",
        description:
          error instanceof AxiosError
            ? error.response?.data?.message
            : (error as Error).message,
        variant: "warning",
      });
    },
    onSuccess: (data) => {
      toast({
        title: "Cadastro efetuado com sucesso",
        description: "Verifique seu WhatsApp para ativar sua conta",
        variant: "success",
      });
      router.replace(`/cadastro/${data.id}`);
    },
  });

  async function onSubmit(data: Register) {
    await registerService.mutateAsync(data);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="md:p-8 w-full flex flex-col items-center gap-y-6 mb-6 md:mb-0"
      >
        <FormFields.InputFormField
          form={form}
          formName="name"
          label={t("label.name")}
          placeholder={t("placeholder.name")}
        />
        <FormFields.InputFormField
          form={form}
          formName="email"
          label={t("label.email")}
          placeholder={t("placeholder.email")}
        />

        <div className="flex flex-col md:flex-row w-full gap-4">
          <div className="flex flex-col w-full md:w-1/2">
            <label htmlFor="whatsapp" className="text-sm font-semibold mb-1">
              {t("label.phone")}
            </label>
            <PhoneInput
              country={"br"}
              value={form.watch("phone")}
              onChange={(value) => {
                const numericValue = value.replace(/\D/g, "");
                form.setValue("phone", numericValue);
              }}
              enableSearch={true}
              searchPlaceholder="Buscar"
              countryCodeEditable={false}
              localization={{ ...ptBRCountries }}
              inputStyle={{
                width: "100%",
                height: "40px",
                borderRadius: "9999px",
                border: "1px solid #e5e7eb",
                backgroundColor: "transparent",
                padding: "0.25rem 0.75rem",
                paddingLeft: "55px",
                fontSize: "1rem",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                transition: "all 0.2s",
                outline: "none",
              }}
              containerStyle={{
                width: "100%",
              }}
              buttonStyle={{
                borderTopLeftRadius: "9999px",
                borderBottomLeftRadius: "9999px",
                width: "50px",
                border: "1px solid rgb(229, 231, 235)",
                paddingLeft: "6px",
              }}
              dropdownStyle={{
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
              inputProps={{
                name: "whatsapp",
                placeholder: t("placeholder.phone"),
                required: true,
              }}
            />
            {form.formState.errors.phone && (
              <FormMessage className="mt-2">
                {form.formState.errors.phone.message}
              </FormMessage>
            )}
          </div>

          <div className="flex flex-col w-full md:w-1/2">
            <label htmlFor="user_intent" className="text-sm font-semibold mb-1">
              {t("label.profile")}
            </label>
            <Controller
              control={form.control}
              name="user_intent"
              rules={{ required: true }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CLIENT">Cliente</SelectItem>
                    <SelectItem value="EMPLOYEE">Funcionário</SelectItem>
                    <SelectItem value="OWNER">Proprietário</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {form.formState.errors.user_intent && (
              <FormMessage className="mt-2">
                {form.formState.errors.user_intent.message}
              </FormMessage>
            )}
          </div>
        </div>

        <FormFields.InputFormField
          form={form}
          formName="password"
          label={t("label.password")}
          placeholder={t("placeholder.password")}
          type="password"
        />
        <Buttons.DefaultButton type="submit" title={t("button")} />
      </form>
    </Form>
  );
};

export default RegisterForm;
